import React, { useState, useEffect } from "react";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { updateProfile } from "services/apiService";
import { toast } from 'react-toastify';

export interface SubscriptionPlan {
  name: string;
  amount_in_inr: string;
  amount_in_usd: string;
  tag_line_for_plan: string;
  interval: string;
  descriptions?: Array<{ point: string }>;
}

export interface Organization {
  name: string;
  slug: string;
  subscription_plan: SubscriptionPlan;
}

export interface Profile {
  email: string;
  full_name: string;
  organization: Organization;
  contact_number: string;
  is_subscribed: boolean;
  date_joined: string;
}


interface GeneralProps {
  profile: Profile;
  updateProfileData: (updatedProfile: Profile) => Promise<void>;
}


const General: React.FC<GeneralProps> = ({ profile, updateProfileData }) => {
  const [loading, setLoading] = useState(false);
  const [contactNumber, setContactNumber] = useState<string>(() => {
    if (profile.contact_number) {
      const parts = profile.contact_number.split("-");
      return parts[1] || "";
    }
    return "";
  });

  const [countryCode, setCountryCode] = useState<string>(() => {
    if (profile.contact_number) {
      const parts = profile.contact_number.split("-");
      return parts[0] || "+91";
    }
    return "+91";
  });

  const [updatedProfile, setUpdatedProfile] = useState<Profile>(profile);

  useEffect(() => {
    setUpdatedProfile(profile);
    if (profile.contact_number) {
      setContactNumber(profile.contact_number.split("-")[1] || "");
      setCountryCode(profile.contact_number.split("-")[0] || "+91");
    }
  }, [profile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setUpdatedProfile(prevProfile => ({
      ...prevProfile,
      [id]: value,
    }));
  };

  const handleCountryCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCountryCode(e.target.value);
    setContactNumber(""); // Reset contact number when changing the country code
  };

  const handleContactNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Ensure only numeric input
    if (!/^\d*$/.test(value)) {
      return; // Ignore non-numeric input
    }

    if (value.length <= 15) {
      setContactNumber(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const updatedData = {
        ...updatedProfile,
        contact_number: `${countryCode}-${contactNumber}`,
      };
      setLoading(true);
      await updateProfile(updatedData);
      // Fetch updated profile
      await updateProfileData(updatedData);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error("Error updating profile:", error);
    }
  };

  return (
    <>
      <Card extra={"w-full h-full p-3"}>
        {/* Header */}
        <div className="mt-2 mb-6 w-full">
          <h4 className="px-2 text-base font-semibold text-[#1F2937] dark:text-white">
            Update Profile
          </h4>
          <p className="mt-1 px-2 text-xs text-[#6B7280] dark:text-gray-300">
            Edit the fields below to update your profile information.
          </p>
        </div>
        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4 px-2">
            {/* Full Name */}
            <InputField
              id="full_name"
              label="Full Name"
              extra="flex flex-col items-start justify-center rounded-2xl bg-white dark:bg-navy-800 bg-clip-border px-3 py-4"
              placeholder="Enter full name"
              variant="auth"
              type="text"
              value={updatedProfile.full_name}
              onChange={handleChange}
            />
            {/* Email */}
            <InputField
              id="email"
              label="Email"
              extra="flex flex-col items-start justify-center rounded-2xl bg-white dark:bg-navy-800 bg-clip-border px-3 py-4"
              placeholder="Enter your email"
              variant="auth"
              type="email"
              value={updatedProfile.email}
              onChange={handleChange}
            />
            
            {/* Contact Number with Country Code */}
            <div className="flex flex-col items-start justify-center rounded-2xl bg-white dark:bg-navy-800 bg-clip-border px-3 py-4">
            <label htmlFor="phone" className="text-sm font-medium text-[#1F2937] dark:text-white mb-2">
              Contact Number
            </label>
            <div className="flex w-full border rounded-lg bg-gray-200 dark:bg-navy-700 ">
              <select
                id="countryCode"
                className="border-none rounded-l-lg px-4 py-3 bg-white-200 dark:bg-navy-700 text-sm"
                value={countryCode}
                onChange={handleCountryCodeChange}
                style={{ minWidth: '80px' }}
              >
                <option value="+1">+1 (USA/Canada)</option>
                <option value="+7">+7 (Russia)</option>
                <option value="+20">+20 (Egypt)</option>
                <option value="+27">+27 (South Africa)</option>
                <option value="+44">+44 (UK)</option>
                <option value="+49">+49 (Germany)</option>
                <option value="+33">+33 (France)</option>
                <option value="+34">+34 (Spain)</option>
                <option value="+39">+39 (Italy)</option>
                <option value="+31">+31 (Netherlands)</option>
                <option value="+41">+41 (Switzerland)</option>
                <option value="+46">+46 (Sweden)</option>
                <option value="+47">+47 (Norway)</option>
                <option value="+48">+48 (Poland)</option>
                <option value="+55">+55 (Brazil)</option>
                <option value="+52">+52 (Mexico)</option>
                <option value="+54">+54 (Argentina)</option>
                <option value="+60">+60 (Malaysia)</option>
                <option value="+61">+61 (Australia)</option>
                <option value="+62">+62 (Indonesia)</option>
                <option value="+63">+63 (Philippines)</option>
                <option value="+64">+64 (New Zealand)</option>
                <option value="+65">+65 (Singapore)</option>
                <option value="+66">+66 (Thailand)</option>
                <option value="+81">+81 (Japan)</option>
                <option value="+82">+82 (South Korea)</option>
                <option value="+84">+84 (Vietnam)</option>
                <option value="+86">+86 (China)</option>
                <option value="+90">+90 (Turkey)</option>
                <option value="+91">+91 (India)</option>
                <option value="+92">+92 (Pakistan)</option>
                <option value="+94">+94 (Sri Lanka)</option>
                <option value="+95">+95 (Myanmar)</option>
                <option value="+971">+971 (UAE)</option>
                <option value="+966">+966 (Saudi Arabia)</option>
                <option value="+968">+968 (Oman)</option>
                <option value="+974">+974 (Qatar)</option>
                <option value="+973">+973 (Bahrain)</option>
                <option value="+965">+965 (Kuwait)</option>
                <option value="+972">+972 (Israel)</option>
                <option value="+98">+98 (Iran)</option>
                <option value="+880">+880 (Bangladesh)</option>
                <option value="+977">+977 (Nepal)</option>
              </select>
              <input
                id="contact_number"
                type="text"
                className="w-full border border-gray-300 rounded-md px-1 py-1 bg-white dark:bg-navy-700 text-sm focus:outline-none focus:border-brand-500"
                placeholder="Enter your Contact Number"
                value={contactNumber}
                onChange={handleContactNumberChange}
              />
            </div>
          </div>
          </div>
          <div className="mt-4 flex justify-end px-2">
            <button
              type="submit"
              className={`mt-6 w-60 rounded-lg ${loading ? 'bg-brand-400' : 'bg-brand-500'} py-2 text-white hover:bg-brand-600`}
            >
               {loading ? (
                <svg 
                className="animate-spin h-5 w-5 mx-auto text-white" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              ) : (
                "Update Profile"
              )}
              
            </button>
          </div>
        </form>
      </Card>
    </>
  );
};

export default General;